import React, { useEffect, useState, useMemo } from 'react';
import {
  DollarSign,
  TrendingDown,
  Server,
  Network,
  Shield,
  Layers,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Search,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useInsightDataPath } from '../hooks/useInsightDataPath';

// =============================================================================
// TYPES
// =============================================================================

interface OptimizationFinding {
  id: string;
  type: string;
  category: string;
  severity: string;
  cloud: 'aws' | 'oci';
  region?: string;
  accountId?: string;
  compartmentId?: string;
  vpcId?: string;
  vpcName?: string;
  vcnId?: string;
  vcnName?: string;
  resourceId?: string;
  resourceName?: string;
  title: string;
  description: string;
  currentState?: Record<string, unknown>;
  recommendedState?: Record<string, unknown>;
  recommendation: string;
  monthlySavings: number;
  yearlySavings: number;
  operationalBenefits?: string[];
}

interface OptimizationReport {
  generatedAt: string;
  summary: {
    totalFindings: number;
    totalMonthlySavings: number;
    totalYearlySavings: number;
    byCategory: Record<string, { count: number; monthlySavings: number }>;
    byType: Record<string, { count: number; monthlySavings: number }>;
  };
  findings: OptimizationFinding[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

const CATEGORY_CONFIG: Record<string, { icon: React.ReactNode; bgColor: string; textColor: string; label: string }> = {
  compute: { 
    icon: <Server className="w-4 h-4" />, 
    bgColor: 'bg-semantic-bg-warning-secondary', 
    textColor: 'text-semantic-text-warning', 
    label: 'Compute' 
  },
  network: { 
    icon: <Network className="w-4 h-4" />, 
    bgColor: 'bg-semantic-bg-brand-secondary', 
    textColor: 'text-semantic-text-brand', 
    label: 'Network' 
  },
  security: { 
    icon: <Shield className="w-4 h-4" />, 
    bgColor: 'bg-semantic-bg-brand-secondary', 
    textColor: 'text-semantic-text-brand', 
    label: 'Security' 
  },
  architecture: { 
    icon: <Layers className="w-4 h-4" />, 
    bgColor: 'bg-semantic-bg-brand-secondary', 
    textColor: 'text-semantic-text-brand', 
    label: 'Architecture' 
  },
};

const TYPE_LABELS: Record<string, string> = {
  'NAT_GATEWAY_REDUNDANT': 'Redundant NAT Gateways',
  'NAT_GATEWAY_ORPHAN': 'Orphan NAT Gateway (No Route Table)',
  'NAT_GATEWAY_UNDERUTILIZED': 'Underutilized NAT Gateway (Empty Subnets)',
  'IGW_NO_ROUTES': 'IGW Without Routes',
  'IGW_EMPTY_SUBNETS': 'IGW with Empty Subnets',
  'SUBNETS_EMPTY': 'Empty Subnets',
  'SECURITY_GROUPS_ORPHAN': 'Orphan Security Groups',
  'ELASTIC_IPS_UNATTACHED': 'Unattached Elastic IPs',
  'VPC_CONSOLIDATION_OPPORTUNITY': 'VPC Consolidation',
  'VCN_CONSOLIDATION_OPPORTUNITY': 'VCN Consolidation',
};

// =============================================================================
// COMPONENTS
// =============================================================================

const SavingsCard: React.FC<{
  title: string;
  monthly: number;
  yearly: number;
  icon: React.ReactNode;
}> = ({ title, monthly, yearly, icon }) => (
  <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-6 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <span className="text-sm font-medium text-semantic-text-secondary">{title}</span>
      <div className="w-10 h-10 bg-semantic-bg-success-secondary rounded-xl flex items-center justify-center text-semantic-text-success">
        {icon}
      </div>
    </div>
    <div className="space-y-1">
      <p className="text-3xl font-bold text-semantic-text-primary">
        ${monthly.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        <span className="text-sm font-normal text-semantic-text-secondary">/mo</span>
      </p>
      <p className="text-sm text-semantic-text-success">
        ${yearly.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/year
      </p>
    </div>
  </div>
);

const CategoryBreakdown: React.FC<{
  byCategory: Record<string, { count: number; monthlySavings: number }>;
}> = ({ byCategory }) => (
  <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-6 shadow-sm">
    <h3 className="text-lg font-semibold text-semantic-text-primary mb-4">Savings by Category</h3>
    <div className="space-y-3">
      {Object.entries(byCategory).map(([category, data]) => {
        const config = CATEGORY_CONFIG[category] || { 
          icon: <AlertTriangle className="w-4 h-4" />, 
          bgColor: 'bg-semantic-bg-tertiary', 
          textColor: 'text-semantic-text-primary', 
          label: category 
        };
        return (
          <div key={category} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 ${config.bgColor} rounded-lg flex items-center justify-center ${config.textColor}`}>
                {config.icon}
              </div>
              <div>
                <p className="font-medium text-semantic-text-primary">{config.label}</p>
                <p className="text-xs text-semantic-text-secondary">{data.count} findings</p>
              </div>
            </div>
            <span className="font-semibold text-semantic-text-success">
              ${data.monthlySavings.toFixed(2)}/mo
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const FindingCard: React.FC<{
  finding: OptimizationFinding;
  expanded: boolean;
  onToggle: () => void;
  onViewTopology: () => void;
}> = ({ finding, expanded, onToggle, onViewTopology }) => {
  const config = CATEGORY_CONFIG[finding.category] || { 
    icon: <AlertTriangle className="w-4 h-4" />, 
    bgColor: 'bg-semantic-bg-tertiary', 
    textColor: 'text-semantic-text-primary', 
    label: finding.category 
  };
  
  return (
    <div className="bg-semantic-bg-secondary rounded-xl border border-semantic-border-primary overflow-hidden">
      {/* Header - Always visible */}
      <div
        className="px-6 py-4 cursor-pointer hover:bg-semantic-bg-tertiary transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start gap-4">
          <button className="mt-1 text-semantic-text-secondary">
            {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${config.bgColor} ${config.textColor} rounded text-xs font-medium`}>
                {config.icon}
                {config.label}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                finding.cloud === 'aws' ? 'bg-semantic-bg-warning-secondary text-semantic-text-warning' : 'bg-semantic-bg-danger-secondary text-semantic-text-danger'
              }`}>
                {finding.cloud.toUpperCase()}
              </span>
              {finding.region && (
                <span className="text-xs text-semantic-text-secondary">{finding.region}</span>
              )}
            </div>
            
            <h4 className="font-medium text-semantic-text-primary">{finding.title}</h4>
            <p className="text-sm text-semantic-text-secondary mt-1 line-clamp-2">{finding.description}</p>
          </div>
          
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-bold text-semantic-text-success">
              ${finding.monthlySavings.toFixed(2)}
              <span className="text-xs font-normal text-semantic-text-secondary">/mo</span>
            </p>
            <p className="text-xs text-semantic-text-secondary">
              ${finding.yearlySavings.toFixed(2)}/yr
            </p>
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="px-6 pb-6 border-t border-semantic-border-secondary bg-semantic-bg-tertiary/50">
          <div className="pt-4 space-y-4">
            {/* Current State */}
            {finding.currentState && (
              <div>
                <h5 className="text-sm font-semibold text-semantic-text-primary mb-2">Current State</h5>
                <pre className="text-xs bg-semantic-bg-tertiary rounded-lg p-3 overflow-x-auto text-semantic-text-primary">
                  {JSON.stringify(finding.currentState, null, 2)}
                </pre>
              </div>
            )}
            
            {/* Operational Benefits (for OCI where savings = 0) */}
            {finding.operationalBenefits && finding.operationalBenefits.length > 0 && finding.monthlySavings === 0 && (
              <div>
                <h5 className="text-sm font-semibold text-semantic-text-primary mb-2">Operational Benefits</h5>
                <ul className="list-disc list-inside text-sm text-semantic-text-secondary bg-semantic-bg-brand-secondary border border-semantic-border-brand-primary rounded-lg p-3 space-y-1">
                  {finding.operationalBenefits.map((benefit, i) => (
                    <li key={i}>{benefit}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Recommendation */}
            <div>
              <h5 className="text-sm font-semibold text-semantic-text-primary mb-2">Recommendation</h5>
              <p className="text-sm text-semantic-text-secondary bg-semantic-bg-success-secondary border border-semantic-border-success-primary rounded-lg p-3">
                {finding.recommendation}
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              {(finding.vpcId || finding.vcnId) && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewTopology();
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-semantic-bg-brand-bold text-semantic-fg-base rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  View in Topology
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const OptimizationHub: React.FC = () => {
  const navigate = useNavigate();
  const dataPath = useInsightDataPath();
  const [data, setData] = useState<OptimizationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    cloud: '' as '' | 'aws' | 'oci',
    category: '',
    minSavings: 0,
  });

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);

    (async () => {
      try {
        const res = await fetch(`${dataPath}/optimization-report.json`);
        if (!res.ok) throw new Error('Failed to load optimization report');
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    })();
  }, [dataPath]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleViewTopology = (finding: OptimizationFinding) => {
    const params = new URLSearchParams();
    params.set('provider', finding.cloud);
    if (finding.region) params.set('region', finding.region);
    
    if (finding.cloud === 'aws') {
      if (finding.accountId) params.set('account', finding.accountId);
      if (finding.vpcId) params.set('vpc', finding.vpcId);
    } else if (finding.cloud === 'oci') {
      if (finding.compartmentId) params.set('account', finding.compartmentId);
      if (finding.vcnId) params.set('vpc', finding.vcnId);
    }
    
    navigate(`/insight/topology?${params.toString()}`);
  };

  const filteredFindings = useMemo(() => {
    if (!data) return [];
    
    return data.findings.filter(f => {
      if (filters.cloud && f.cloud !== filters.cloud) return false;
      if (filters.category && f.category !== filters.category) return false;
      if (f.monthlySavings < filters.minSavings) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchable = [f.title, f.description, f.vpcName, f.vcnName, f.resourceName, f.type]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      return true;
    });
  }, [data, filters, searchQuery]);

  const clearFilters = () => {
    setSearchQuery('');
    setFilters({ cloud: '', category: '', minSavings: 0 });
  };

  const hasActiveFilters = searchQuery || filters.cloud || filters.category || filters.minSavings > 0;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-semantic-bg-brand-bold" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-semantic-text-danger mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-semantic-text-primary">Failed to load optimization data</h2>
          <p className="text-semantic-text-secondary mt-2">{error}</p>
          <p className="text-sm text-semantic-text-secondary mt-4">
            Run: <code className="bg-semantic-bg-tertiary px-2 py-1 rounded text-semantic-text-primary">node scarif/generateOptimizationReport.js</code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-semantic-bg-primary min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-semantic-text-primary">Optimization Hub</h1>
        <p className="text-sm text-semantic-text-secondary mt-1">
          {data.summary.totalFindings} optimization opportunities found | 
          Last analyzed: {new Date(data.generatedAt).toLocaleString()}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <SavingsCard
          title="Total Potential Savings"
          monthly={data.summary.totalMonthlySavings}
          yearly={data.summary.totalYearlySavings}
          icon={<DollarSign className="w-5 h-5" />}
        />
        <CategoryBreakdown byCategory={data.summary.byCategory} />
        <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-semantic-text-primary mb-4">Top Opportunities</h3>
          <div className="space-y-3">
            {Object.entries(data.summary.byType)
              .sort((a, b) => b[1].monthlySavings - a[1].monthlySavings)
              .slice(0, 4)
              .map(([type, info]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-sm text-semantic-text-secondary">{TYPE_LABELS[type] || type}</span>
                  <span className="text-sm font-medium text-semantic-text-success">
                    ${info.monthlySavings.toFixed(2)}/mo
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-semantic-text-secondary" />
            <input
              type="text"
              placeholder="Search findings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-semantic-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-semantic-outline-primary bg-semantic-bg-secondary text-semantic-text-primary placeholder:text-semantic-text-secondary"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-semantic-text-secondary hover:text-semantic-text-primary"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            value={filters.cloud}
            onChange={(e) => setFilters(f => ({ ...f, cloud: e.target.value as '' | 'aws' | 'oci' }))}
            className="border border-semantic-border-primary rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-semantic-outline-primary bg-semantic-bg-secondary text-semantic-text-primary"
          >
            <option value="">All Clouds</option>
            <option value="aws">AWS</option>
            <option value="oci">OCI</option>
          </select>
          <select
            value={filters.category}
            onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
            className="border border-semantic-border-primary rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-semantic-outline-primary bg-semantic-bg-secondary text-semantic-text-primary"
          >
            <option value="">All Categories</option>
            <option value="compute">Compute</option>
            <option value="network">Network</option>
            <option value="security">Security</option>
            <option value="architecture">Architecture</option>
          </select>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-semantic-text-secondary hover:text-semantic-text-primary"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Findings List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-semantic-text-secondary">
            Showing {filteredFindings.length} of {data.findings.length} findings
          </p>
        </div>

        {filteredFindings.map(finding => (
          <FindingCard
            key={finding.id}
            finding={finding}
            expanded={expandedIds.has(finding.id)}
            onToggle={() => toggleExpand(finding.id)}
            onViewTopology={() => handleViewTopology(finding)}
          />
        ))}
        
        {filteredFindings.length === 0 && (
          <div className="text-center py-12 bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary">
            <TrendingDown className="w-12 h-12 text-semantic-text-secondary mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-semantic-text-primary">No findings match your filters</h3>
            <p className="text-sm text-semantic-text-secondary">Try adjusting your search or filters</p>
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 bg-semantic-bg-brand-bold text-semantic-fg-base rounded-lg text-sm font-medium hover:opacity-90"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OptimizationHub;

